/**
 * 異常に長いのでファイル分割
 */
import { ScriptableScaleContext } from 'chart.js/auto'
import { TimeUnit } from 'chart.js/types/adapters'
import moment from 'moment'
import { ScaleOption } from '@/lib/graph/IWSChart/IWSChart'
import { XRange } from '@/lib/graph/types/initResult'
import { DefinitionInfoGraph, DefinitionInfoGraphThresholdItem } from '@/models/initData/DefinitionInfo'

export const getAxes = (
  display: boolean,
  fields: string[],
  xRangeType: string,
  units: string[],
  threshold: DefinitionInfoGraph['threshold'],
  selectedField?: string
): ScaleOption => {
  const timeFormat: { hour?: string; day?: string; minute?: string; month?: string } = {}
  let timeUnit!: string
  switch (xRangeType) {
    case XRange.HOUR:
      timeFormat.minute = 'HH:mm'
      timeUnit = 'minute'
      break
    case XRange.DAY:
      timeFormat.hour = 'H'
      timeUnit = 'hour'
      break
    case XRange.MONTH:
      timeFormat.day = 'M/D'
      timeUnit = 'day'
      break
    case XRange.YEAR:
      timeFormat.month = 'Y/M'
      timeUnit = 'month'
      break
  }

  const axes: ScaleOption = {
    '℃': {
      suggestedMin: -10,
      suggestedMax: 40,
      scaleLabel: {
        labelString: '℃',
        labelColor: 'red',
      },
      grid: {
        drawTicks: false,
        drawBorder: false,
      },
      axis: 'y',
      display,
      position: 'left',
      ticks: {
        padding: 15,
        stepSize: 10,
      },
    },
    '%': {
      suggestedMin: 0,
      suggestedMax: 100,
      scaleLabel: {
        labelString: '%',
        labelColor: 'blue',
      },
      axis: 'y',
      grid: {
        drawTicks: false,
        drawBorder: false,
      },
      display,
      ticks: {
        padding: 15,
        stepSize: 20,
      },
    },
    hPa: {
      scaleLabel: {
        labelString: 'hPa',
        labelColor: 'purple',
      },
      axis: 'y',
      grid: {
        drawTicks: false,
        drawBorder: false,
      },
      display,
      position: 'right',
      ticks: {
        padding: 15,
      },
    },
    mm: {
      beginAtZero: true,
      scaleLabel: {
        labelString: 'mm',
        labelColor: 'purple',
      },
      axis: 'y',
      grid: {
        drawTicks: false,
        drawBorder: false,
      },
      display,
      position: fields.length === 1 ? 'left' : 'right',
      ticks: {
        padding: 15,
      },
    },
    m: {
      beginAtZero: selectedField && selectedField.indexOf('rain') >= 0 ? true : false,
      suggestedMax: 5,
      scaleLabel: {
        labelString: 'm',
        labelColor: 'orange',
      },
      axis: 'y',
      grid: {
        drawTicks: false,
        drawBorder: false,
        borderWidth: 2,
      },
      display,
      position: fields.length === 1 ? 'left' : 'right',
      ticks: {
        padding: 15,
      },
    },
    'm/s': {
      suggestedMax: 50,
      suggestedMin: 0,
      beginAtZero: true,
      scaleLabel: {
        labelString: 'm/s',
        labelColor: 'orange',
      },
      axis: 'y',
      grid: {
        drawTicks: false,
        drawBorder: false,
        borderWidth: 2,
      },
      display,
      position: 'left',
      ticks: {
        padding: 15,
      },
    },
    marker: {
      suggestedMin: 0,
      suggestedMax: 50,
      grid: {
        drawTicks: false,
        display: false,
      },
      axis: 'y',
      display: false,
      ticks: {
        display: false,
      },
    },
    x: {
      axis: 'x',
      position: 'bottom',
      type: 'time',
      time: {
        tooltipFormat: 'YYYY/M/D H:mm',
        stepSize: display && xRangeType == XRange.HOUR ? 10 : 1,
        displayFormats: timeFormat,
        unit: timeUnit as TimeUnit,
      },
      grid: {
        display: true,
        drawTicks: false,
        drawBorder: display ? true : false,
        borderWidth: 2,
        offset: false,
        borderColor: '#34495E',
        lineWidth: () => {
          return display ? 1 : 1
        },
        color(val: ScriptableScaleContext) {
          switch (xRangeType) {
            case XRange.HOUR:
              return display
                ? '#bbbbbb'
                : moment(val.tick.value).minute() == 0 && moment(val.tick.value).hour() % 8 == 0
                ? '#34495E'
                : moment(val.tick.value).minute() === 0
                ? '#bbbbbb'
                : ''
            case XRange.DAY:
              return display
                ? '#bbbbbb'
                : moment(val.tick.value).hour() == 0 && moment(val.tick.value).minute() == 0
                ? '#34495E'
                : moment(val.tick.value).hour() % 3 === 0 && moment(val.tick.value).minute() == 0
                ? '#bbbbbb'
                : ''
            case XRange.MONTH:
              return display
                ? '#bbbbbb'
                : moment(val.tick.value).date() == 1
                ? '#34495E'
                : moment(val.tick.value).date() % 3 === 0
                ? '#bbbbbb'
                : ''
            case XRange.YEAR:
              return display
                ? '#bbbbbb'
                : moment(val.tick.value).month() == 0
                ? '#34495E'
                : moment(val.tick.value).month() % 3 === 0
                ? '#bbbbbb'
                : ''
            default:
              return '#bbbbbb'
          }
        },
      },
      ticks: {
        callback: (_val: string | number) => {
          const val = _val.toString()
          switch (xRangeType) {
            case XRange.HOUR:
              return Number(val.split(':')[1]) == 0 ? val : ''
            case XRange.DAY:
              return Number(val) % 3 == 0 ? val + ':00' : ''
            case XRange.MONTH:
              return Number(val.split('/')[1]) % 3 == 1 ? val : ''
            case XRange.YEAR:
              return Number(val.split('/')[1]) == 1
                ? val.split('/')[0] + '年' + val.split('/')[1] + '月'
                : val.split('/')[1] + '月'
            default:
              return ''
          }
        },
        maxRotation: 0,
        minRotation: 0,
        display,
        z: 1000, //ここ指定しないと+のはみ出しを消す操作時にticksも消えてしまいます
      },
    },
  }

  for (const key of Object.keys(axes)) {
    if (
      units.find(unit => axes[key as keyof typeof axes].scaleLabel?.labelString === unit) ||
      key === 'x' ||
      key === 'marker'
    ) {
      continue
    }
    delete axes[key]
  }
  const colorConfig = threshold?.[xRangeType]?.[fields[0]]
  const axis = axes[units[0]]

  if (colorConfig && axis && axis.grid) {
    axis.grid.color = (val: ScriptableScaleContext) => {
      const conf = colorConfig.find(
        (config: DefinitionInfoGraphThresholdItem) => config?.value == Number(val?.tick?.value)
      )
      if (conf) return conf.color
      return '#dddddd'
    }
    axis.grid.lineWidth = (val: ScriptableScaleContext) => {
      const conf = colorConfig.find(
        (config: DefinitionInfoGraphThresholdItem) => config?.value == Number(val?.tick?.value)
      )
      if (conf) return conf.thickness
      return 1
    }
  }

  return axes
}
